import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { EditorState, ContentState, convertFromHTML } from 'draft-js';
import { convertHTMlToRaw } from '../../../utils/HTMLDraft';
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../../utils/sentryHelpers';
import { uploadPublicDocument, validatePublicDocument } from '../../../utils/uploadPublicDocument';

const client = generateClient();

export function useNewsForm({ companyId, newsId, currentIndustry }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isEditMode = !!newsId;

  // Core news data
  const [newsData, setNewsData] = useState({
    name: [],
    content: [],
    image: {},
    file: {},
    file3DModel: {},
    cMSNewsEmployeeId: "",
    cMSNewsTopicId: "",
    conditions: [],
  });

  // Loading & error
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Validation
  const [touchedFields, setTouchedFields] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Topics
  const [topics, setTopics] = useState([]);
  const [topicGroups, setTopicGroups] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectTopicsModal, setSelectTopicsModal] = useState(false);
  const [unknownTopicNames, setUnknownTopicNames] = useState({});

  // Employee
  const [employee, setEmployee] = useState(null);
  const [selectEmployeeModal, setSelectEmployeeModal] = useState(false);

  // Editor (Draft.js)
  const [editorState, setEditorState] = useState({
    de: EditorState.createEmpty(),
    en: EditorState.createEmpty(),
  });

  // Image upload
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadingImageError, setUploadingImageError] = useState(null);

  // File upload
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadingFileError, setUploadingFileError] = useState(null);

  // 3D Model (deferred upload)
  const [pending3DModelFile, setPending3DModelFile] = useState(null);
  const [preview3DModelUrl, setPreview3DModelUrl] = useState(null);
  const [upload3DModelError, setUpload3DModelError] = useState(null);

  // ─── Validation ───────────────────────────────────────────────

  const SECTION_FIELDS = {
    1: ['cMSNewsTopicId'],
    2: ['name_de', 'name_en', 'content_de', 'content_en'],
    3: ['image', 'imageCondition', 'file3DModelCondition', 'fileCondition'],
    4: ['cMSNewsEmployeeId'],
  };

  const validateField = useCallback((fieldName) => {
    let error = null;
    switch (fieldName) {
      case 'cMSNewsTopicId':
        if (!newsData.cMSNewsTopicId) error = t("Please select a topic");
        break;
      case 'name_de':
        if (!newsData.name?.find(n => n.language === "de")?.text) error = t("Please enter a German title");
        break;
      case 'name_en':
        if (!newsData.name?.find(n => n.language === "en")?.text) error = t("Please enter an English title");
        break;
      case 'content_de':
        if (!newsData.content?.find(c => c.language === "de")?.text) error = t("Please enter German content");
        break;
      case 'content_en':
        if (!newsData.content?.find(c => c.language === "en")?.text) error = t("Please enter English content");
        break;
      case 'image':
        if (!newsData.image?.fileName) error = t("Please upload an image");
        break;
      case 'imageCondition':
        if (newsData.image?.fileName && !newsData.conditions?.find(c => c.name === "image")?.accepted)
          error = t("Please agree to the image conditions");
        break;
      case 'file3DModelCondition':
        if ((newsData.file3DModel?.fileName || pending3DModelFile) && !newsData.conditions?.find(c => c.name === "file3DModel")?.accepted)
          error = t("Please agree to the 3D model conditions");
        break;
      case 'fileCondition':
        if (newsData.file?.fileName && !newsData.conditions?.find(c => c.name === "file")?.accepted)
          error = t("Please agree to the file conditions");
        break;
      case 'cMSNewsEmployeeId':
        if (!newsData.cMSNewsEmployeeId) error = t("Please select an employee");
        break;
    }
    setValidationErrors(prev => ({ ...prev, [fieldName]: error }));
    return error;
  }, [newsData, pending3DModelFile, t]);

  const markFieldTouched = useCallback((fieldName) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    validateField(fieldName);
  }, [validateField]);

  const validateSection = useCallback((sectionNumber) => {
    const fields = SECTION_FIELDS[sectionNumber] || [];
    const newTouched = {};
    const errors = {};
    let hasErrors = false;

    for (const field of fields) {
      newTouched[field] = true;
      let error = null;
      switch (field) {
        case 'cMSNewsTopicId':
          if (!newsData.cMSNewsTopicId) error = t("Please select a topic");
          break;
        case 'name_de':
          if (!newsData.name?.find(n => n.language === "de")?.text) error = t("Please enter a German title");
          break;
        case 'name_en':
          if (!newsData.name?.find(n => n.language === "en")?.text) error = t("Please enter an English title");
          break;
        case 'content_de':
          if (!newsData.content?.find(c => c.language === "de")?.text) error = t("Please enter German content");
          break;
        case 'content_en':
          if (!newsData.content?.find(c => c.language === "en")?.text) error = t("Please enter English content");
          break;
        case 'image':
          if (!newsData.image?.fileName) error = t("Please upload an image");
          break;
        case 'imageCondition':
          if (newsData.image?.fileName && !newsData.conditions?.find(c => c.name === "image")?.accepted)
            error = t("Please agree to the image conditions");
          break;
        case 'file3DModelCondition':
          if ((newsData.file3DModel?.fileName || pending3DModelFile) && !newsData.conditions?.find(c => c.name === "file3DModel")?.accepted)
            error = t("Please agree to the 3D model conditions");
          break;
        case 'fileCondition':
          if (newsData.file?.fileName && !newsData.conditions?.find(c => c.name === "file")?.accepted)
            error = t("Please agree to the file conditions");
          break;
        case 'cMSNewsEmployeeId':
          if (!newsData.cMSNewsEmployeeId) error = t("Please select an employee");
          break;
      }
      errors[field] = error;
      if (error) hasErrors = true;
    }

    setTouchedFields(prev => ({ ...prev, ...newTouched }));
    setValidationErrors(prev => ({ ...prev, ...errors }));
    return !hasErrors;
  }, [newsData, pending3DModelFile, t]);

  const validateAll = useCallback(() => {
    let allValid = true;
    for (let section = 1; section <= 4; section++) {
      if (!validateSection(section)) allValid = false;
    }
    return allValid;
  }, [validateSection]);

  const validateAllAndGetFirstInvalid = useCallback(() => {
    setSubmitAttempted(true);
    let firstInvalid = null;
    for (let section = 1; section <= 4; section++) {
      if (!validateSection(section) && firstInvalid === null) {
        firstInvalid = section;
      }
    }
    return firstInvalid;
  }, [validateSection]);

  const isSectionComplete = useCallback((sectionNumber) => {
    switch (sectionNumber) {
      case 1:
        return !!newsData.cMSNewsTopicId;
      case 2:
        return !!(
          newsData.name?.find(n => n.language === "de")?.text &&
          newsData.name?.find(n => n.language === "en")?.text &&
          newsData.content?.find(c => c.language === "de")?.text &&
          newsData.content?.find(c => c.language === "en")?.text
        );
      case 3: {
        const hasImage = !!newsData.image?.fileName;
        const imageCondOk = !hasImage || !!newsData.conditions?.find(c => c.name === "image")?.accepted;
        const has3DModel = !!(newsData.file3DModel?.fileName || pending3DModelFile);
        const file3DModelCondOk = !has3DModel || !!newsData.conditions?.find(c => c.name === "file3DModel")?.accepted;
        const fileCondOk = !newsData.file?.fileName || !!newsData.conditions?.find(c => c.name === "file")?.accepted;
        return hasImage && imageCondOk && file3DModelCondOk && fileCondOk;
      }
      case 4:
        return !!newsData.cMSNewsEmployeeId;
      default:
        return false;
    }
  }, [newsData]);

  const getFieldError = useCallback((fieldName) => {
    if (!touchedFields[fieldName]) return null;
    return validationErrors[fieldName] || null;
  }, [touchedFields, validationErrors]);

  // ─── GraphQL Operations ───────────────────────────────────────

  const getNewsData = async () => {
    // Only fetch fields the form actually consumes. Nested objects (company,
    // employee, topic, …) have stricter auth — fetching them would fail for
    // non-admin users, and the form derives their display data separately
    // via getEmployee() and getTopics().
    const query = /* GraphQL */ `
      query GetCMSNews($id: ID!) {
        getCMSNews(id: $id) {
          id
          name { language text }
          content { language text }
          image { fileName alt }
          file { url s3Path fileName type name }
          file3DModel { url s3Path fileName type name }
          conditions { name accepted }
          cMSNewsEmployeeId
          cMSNewsTopicId
          status
          isHero
        }
      }
    `;

    let response;
    try {
      response = await client.graphql({ variables: { id: newsId }, query });
    } catch (error) {
      // Amplify v6 throws on any GraphQL error, even when data is partially
      // populated. If the news record itself came through, use it and log the
      // partial errors as a warning rather than failing the whole edit page.
      if (error?.data?.getCMSNews) {
        console.warn("Partial errors loading news, continuing with available data", error.errors);
        captureError(error, {
          feature: 'news',
          operation: 'get-news-data-partial',
          context: { newsId },
          tags: { component: 'CreateNews', severity: 'warning' }
        });
        response = error;
      } else {
        console.warn("Error getting news data", error);
        captureError(error, {
          feature: 'news',
          operation: 'get-news-data',
          context: { newsId },
          tags: { component: 'CreateNews' }
        });
        return;
      }
    }

    return response.data.getCMSNews;
  };

  const initialLoadNews = async () => {
    setIsLoading(true);
    const dbNews = await getNewsData();
    if (!dbNews) {
      setIsLoading(false);
      setLoadError(true);
      return;
    }
    setNewsData(dbNews);
    if (dbNews.content) {
      const deContent = dbNews.content.find(c => c.language === "de")?.text;
      const enContent = dbNews.content.find(c => c.language === "en")?.text;
      setEditorState({
        de: deContent ? EditorState.createWithContent(
          ContentState.createFromBlockArray(
            convertFromHTML(deContent)
          )
        ) : EditorState.createEmpty(),
        en: enContent ? EditorState.createWithContent(
          ContentState.createFromBlockArray(
            convertFromHTML(enContent)
          )
        ) : EditorState.createEmpty(),
      });
    }
    setIsLoading(false);
  };

  // Initial news load
  useEffect(() => {
    if (!companyId) {
      console.warn("No company id provided");
      return;
    } else if (!newsId) {
      console.log("No news id provided");
      return;
    } else {
      initialLoadNews();
    }
  }, []);

  // ─── Topics ───────────────────────────────────────────────────

  useEffect(() => {
    if (newsData.cMSNewsTopicId) {
      setSelectedTopics([newsData.cMSNewsTopicId]);
    }
  }, [newsData.cMSNewsTopicId]);

  const toggleSelectTopicsModal = () => setSelectTopicsModal(prev => !prev);

  const handleSetSelectedTopics = (topics) => {
    setSelectedTopics(topics);
    // TODO [CP-1221]: Backend supports only 1 topic (cMSNewsTopicId). When migrated to @manyToMany, save all topics.
    setNewsData(oldData => ({ ...oldData, cMSNewsTopicId: topics[0] || "" }));
    setValidationErrors(prev => ({ ...prev, cMSNewsTopicId: null }));
  };

  const getTopics = async () => {
    const filter = currentIndustry?.id ? { industryId: { eq: currentIndustry.id } } : undefined;
    try {
      const topicGroupsWithTopics = await client.graphql({
        query: /* GraphQL */ `
          query ListTopicGroups(
            $filter: ModelTopicGroupFilterInput
            $limit: Int
            $nextToken: String
          ) {
            listTopicGroups(filter: $filter, limit: $limit, nextToken: $nextToken) {
              items {
                id
                color
                name {
                  language
                  text
                }
                topics {
                  items {
                    id
                    name {
                      language
                      text
                    }
                    topicGroupTopicsId
                  }
                }
              }
              nextToken
              __typename
            }
          }
        `,
        variables: {
          filter,
          limit: 1000
        }
      });
      setTopicGroups(topicGroupsWithTopics.data.listTopicGroups.items);
      const topics = topicGroupsWithTopics.data.listTopicGroups.items.map((topicGroup) => topicGroup.topics.items).flat();
      setTopics(topics);

    } catch (error) {
      console.log("error on fetching topics", error);
      captureError(error, {
        feature: 'news',
        operation: 'fetch-topics',
        tags: { component: 'CreateNews' }
      });
    }
  };

  useEffect(() => {
    if (currentIndustry?.id) {
      getTopics();
    }
  }, [currentIndustry?.id]);

  useEffect(() => {
    const unknownIds = selectedTopics.filter(id => topics.length > 0 && !topics.find(t => t.id === id));
    if (unknownIds.length === 0) return;
    const fetchUnknownTopics = async () => {
      const names = {};
      for (const id of unknownIds) {
        try {
          const res = await client.graphql({
            query: /* GraphQL */ `query GetTopic($id: ID!) { getTopic(id: $id) { id name { language text } } }`,
            variables: { id }
          });
          if (res.data.getTopic?.name) names[id] = res.data.getTopic.name;
        } catch (e) { /* topic may have been deleted */ }
      }
      setUnknownTopicNames(names);
    };
    fetchUnknownTopics();
  }, [selectedTopics, topics]);

  // ─── Employee ─────────────────────────────────────────────────

  const toggleSelectEmployeeModal = () => setSelectEmployeeModal(prev => !prev);

  const handleSetEmployee = (employees) => {
    setNewsData(oldData => ({ ...oldData, cMSNewsEmployeeId: employees[0] }));
    setValidationErrors(prev => ({ ...prev, cMSNewsEmployeeId: null }));
  };

  const getEmployee = async () => {
    try {
      const employee = await client.graphql({
        query: /* GraphQL */ `
        query GetEmployee($id: ID!) {
            getEmployee(id: $id) {
              id
              user {
                id
                firstName
                lastName
                title
              }
              image {
                fileName
                alt
              }
            }
          }
        `,
        variables: { id: newsData.cMSNewsEmployeeId },
      });
      setEmployee(employee.data.getEmployee);
    } catch (error) {
      console.log("error on fetching employee", error);
      captureError(error, {
        feature: 'news',
        operation: 'fetch-employee',
        context: { employeeId: newsData.cMSNewsEmployeeId },
        tags: { component: 'CreateNews' }
      });
    }
  };

  useEffect(() => {
    if (newsData.cMSNewsEmployeeId) {
      getEmployee();
    }
  }, [newsData.cMSNewsEmployeeId]);

  // ─── Editor ───────────────────────────────────────────────────

  const onEditorStateChange = (editorState, language) => {
    setEditorState(oldState => ({ ...oldState, [language]: editorState }));
    setNewsData(oldData => ({ ...oldData, content: [...oldData.content.filter(c => c.language !== language), { language, text: convertHTMlToRaw(editorState) }] }));
  };

  // ─── News Name ────────────────────────────────────────────────

  const setNewsName = (language, text) => {
    setNewsData(oldData => ({
      ...oldData,
      name: [{ language, text }, ...oldData.name.filter(n => n.language !== language)]
    }));
  };

  // ─── Conditions ───────────────────────────────────────────────

  const handleConditionChange = (conditionName, accepted) => {
    setNewsData(oldData => ({
      ...oldData,
      conditions: [{ name: conditionName, accepted }, ...oldData.conditions?.filter(c => c.name !== conditionName)]
    }));
  };

  // ─── Image Upload ─────────────────────────────────────────────

  const handleSetImage = (file) => {
    if (file) {
      try {
        setIsUploadingImage(true);
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onerror = error => {
          console.log('Error reader: ', error);
          setUploadingImageError(error);
        }
        reader.onload = async () => {
          try {
            const base64FileBuffer = reader.result.replace(/^data:.+;base64,/, '');
            const response = await client.graphql({
              query: /* GraphQL */ `
          mutation UploadCompanyImage($imageBuffer: String, $imageType: String) {
            uploadCompanyImage(imageBuffer: $imageBuffer, imageType: $imageType)
          }
        `,
              variables: {
                imageBuffer: base64FileBuffer,
                imageType: "newsImage",
              },
            });
            const fileName = response.data.uploadCompanyImage.replace(/{|}|fileName=/g, '');
            setNewsData(oldData => ({ ...oldData, image: { fileName, alt: "News Image" } }));

          } catch (error) {
            console.log("error", error);
            captureError(error, {
              feature: 'news',
              operation: 'upload-image-s3',
              context: { companyId },
              tags: { component: 'CreateNews' }
            });
            setIsUploadingImage(false);
            setUploadingImageError(error.errors[0].message);
          }
        }
        setIsUploadingImage(false);
        setUploadingImageError(null);
      } catch (error) {
        console.log("error", error);
        captureError(error, {
          feature: 'news',
          operation: 'upload-image-reader',
          context: { companyId },
          tags: { component: 'CreateNews' }
        });
        setIsUploadingImage(false);
        setUploadingImageError(error.errors[0].message);
      }
    }
  };

  // ─── File Upload ──────────────────────────────────────────────

  const handleSetFile = async (file) => {
    if (!file) return;
    const validationError = validatePublicDocument(file, t);
    if (validationError) {
      setUploadingFileError(validationError);
      return;
    }

    setIsUploadingFile(true);
    setUploadingFileError(null);
    try {
      const uploadedFile = await uploadPublicDocument(file, { companyId, fileType: "newsFile" });
      setNewsData(oldData => ({ ...oldData, file: uploadedFile }));
    } catch (error) {
      captureError(error, {
        feature: 'news',
        operation: 'upload-file-s3',
        context: { companyId, fileName: file.name, fileSize: file.size },
        tags: { component: 'CreateNews' }
      });
      setUploadingFileError(error?.errors?.[0]?.message || t("Upload failed. Please try again."));
    } finally {
      setIsUploadingFile(false);
    }
  };

  // ─── 3D Model ─────────────────────────────────────────────────

  const handleSet3DModel = (file) => {
    if (preview3DModelUrl) URL.revokeObjectURL(preview3DModelUrl);
    setPending3DModelFile(file);
    setPreview3DModelUrl(URL.createObjectURL(file));
  };

  const handleRemove3DModel = () => {
    if (preview3DModelUrl) URL.revokeObjectURL(preview3DModelUrl);
    setPending3DModelFile(null);
    setPreview3DModelUrl(null);
    setNewsData(oldData => ({ ...oldData, file3DModel: {} }));
  };

  // ─── Submit ───────────────────────────────────────────────────

  const createOrUpdateNews = async () => {
    try {
      setSubmitAttempted(true);
      if (!validateAll()) {
        return;
      }
      setIsSaving(true);

      // Upload 3D model if pending
      let file3DModelData = newsData.file3DModel;
      if (pending3DModelFile) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.replace(/^data:.+;base64,/, ''));
          reader.onerror = reject;
          reader.readAsDataURL(pending3DModelFile);
        });
        const uploadResponse = await client.graphql({
          query: /* GraphQL */ `
            mutation UploadCompanyImage($imageBuffer: String, $imageType: String) {
              uploadCompanyImage(imageBuffer: $imageBuffer, imageType: $imageType)
            }
          `,
          variables: { imageBuffer: base64, imageType: "product3DModel" },
        });
        const raw = uploadResponse.data.uploadCompanyImage;
        if (raw?.includes('error=')) {
          setUpload3DModelError(raw.replace(/{|}|error=/g, ''));
          setIsSaving(false);
          return;
        }
        setUpload3DModelError(null);
        const fileName = raw.replace(/{|}|fileName=/g, '');
        if (fileName) {
          file3DModelData = { fileName, type: "news3DModel" };
        }
      }

      const response = await client.graphql({
        query: newsId ? /* GraphQL */ `
        mutation UpdateCMSNews($input: UpdateCMSNewsInput!) {
          updateCMSNews(input: $input) {
            id
          }
        }
        ` : /* GraphQL */ `
        mutation CreateCMSNews(
          $input: CreateCMSNewsInput!
          $condition: ModelCMSNewsConditionInput
        ) {
          createCMSNews(input: $input, condition: $condition) {
              id
            }
          }
        `,
        variables: {
          input: {
            companyNewsId: companyId,
            name: newsData.name,
            content: newsData.content,
            image: newsData.image,
            file: newsData.file,
            ...(file3DModelData?.fileName ? { file3DModel: file3DModelData } : {}),
            cMSNewsEmployeeId: newsData.cMSNewsEmployeeId,
            cMSNewsTopicId: newsData.cMSNewsTopicId,
            conditions: newsData.conditions,
            ...(newsId ? {} : { status: "created" }),
            ...newsId ? { id: newsId } : {},
          },
        },
      });

      // If it's a new news article, connect it with the current industry
      if (!newsId && currentIndustry && currentIndustry.id) {
        const createdNewsId = response.data.createCMSNews.id;
        await client.graphql({
          query: /* GraphQL */ `
          mutation CreateIndustryNews(
            $input: CreateIndustryNewsInput!
          ) {
            createIndustryNews(input: $input) {
              id
              cMSNewsId
              industryId
            }
          }
          `,
          variables: {
            input: {
              cMSNewsId: createdNewsId,
              industryId: currentIndustry.id
            }
          }
        });
      }

      setTimeout(() => {
        navigate(`/mycompany/${companyId}/news`);
        setIsSaving(false);
      }, 2000);
    } catch (error) {
      setIsSaving(false);
      console.log("error", error);
      captureError(error, {
        feature: 'news',
        operation: newsId ? 'update-news' : 'create-news',
        context: { newsId, companyId },
        tags: { component: 'CreateNews' }
      });
    }
  };

  return {
    // Data
    newsData,
    isLoading,
    loadError,
    isSaving,
    isEditMode,

    // Validation
    validationErrors,
    touchedFields,
    markFieldTouched,
    validateField,
    validateSection,
    validateAll,
    validateAllAndGetFirstInvalid,
    getFieldError,
    submitAttempted,
    isSectionComplete,

    // News data handlers
    setNewsName,
    onEditorStateChange,
    editorState,
    handleConditionChange,

    // Topics
    topics,
    topicGroups,
    selectedTopics,
    unknownTopicNames,
    selectTopicsModal,
    setSelectTopicsModal,
    toggleSelectTopicsModal,
    handleSetSelectedTopics,

    // Employee
    employee,
    selectEmployeeModal,
    setSelectEmployeeModal,
    toggleSelectEmployeeModal,
    handleSetEmployee,

    // Image upload
    handleSetImage,
    isUploadingImage,
    uploadingImageError,

    // File upload
    handleSetFile,
    isUploadingFile,
    uploadingFileError,

    // 3D Model
    pending3DModelFile,
    preview3DModelUrl,
    upload3DModelError,
    handleSet3DModel,
    handleRemove3DModel,

    // Submit
    createOrUpdateNews,
  };
}
